import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounceValue } from 'usehooks-ts'

import { usePartnerFilter } from '../../../programs/grantsProgramsTable/hooks/usePartnerFilter'

import {
  Button,
  Flex,
  SearchFilterList,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TablePagination,
  TableProvider,
  TableRow,
  TableSearchAndFilters
} from '@percent/lemonade'
import { useGrantsPermissions } from '@percent/admin-dashboard/app/grants/hooks/useGrantsPermissions'
import { FormsTableProps } from './FormsTable.types'

export function FormsTable({
  data,
  totalResults,
  isLoading,
  nextPage,
  previousPage,
  queryParams,
  setQueryParams,
  onCreateForm,
  onEditForm
}: Readonly<FormsTableProps>) {
  const { t } = useTranslation()
  const [canCreateForm] = useGrantsPermissions(['can_create_form'])
  const [searchValue, setSearchValue] = useState(queryParams.search ?? '')
  const [debouncedSearch] = useDebounceValue(searchValue, 500)
  const [formIdsValue, setFormIdsValue] = useState((queryParams.formIds ?? []).filter(Boolean).join(', '))
  const [debouncedFormIds] = useDebounceValue(formIdsValue, 500)

  const { selectedPartner, partnerNameOptions, onPartnerChange, isPartnerNameFilterLoading, partnerSearch } =
    usePartnerFilter({ queryParams, setQueryParams })

  useEffect(() => {
    setQueryParams({ search: debouncedSearch || undefined })
  }, [debouncedSearch, setQueryParams])

  useEffect(() => {
    const ids = debouncedFormIds
      .split(/[,\s]+/)
      .map((id: string) => id.trim())
      .filter(Boolean)
    setQueryParams({ formIds: ids.length > 0 ? ids : undefined })
  }, [debouncedFormIds, setQueryParams])

  const columns: TableColumn[] = [
    {
      id: 'formName',
      content: t('grants.forms.formsList.column.formName'),
      isSortable: false,
      props: { width: '20%' }
    },
    {
      id: 'partner',
      content: t('grants.forms.formsList.column.partner'),
      isSortable: false,
      props: { width: '15%' }
    },
    {
      id: 'linkedTasks',
      content: t('grants.forms.formsList.column.linkedTasks'),
      isSortable: false,
      props: { width: '12%' }
    },
    {
      id: 'questions',
      content: t('grants.forms.formsList.column.questions'),
      isSortable: false,
      props: { width: '12%' }
    },
    {
      id: 'formId',
      content: t('grants.forms.formsList.column.formId'),
      isSortable: false,
      props: { width: '20%' }
    },
    {
      id: 'createdAt',
      content: t('grants.forms.formsList.column.createdAt'),
      isSortable: false,
      props: { width: '16%' }
    }
  ]

  return (
    <TableProvider
      columns={columns}
      emptyTableTitle={t('grants.forms.formsList.empty')}
      illustration="no-results"
      isLoading={isLoading}
      totalResults={totalResults}
    >
      <TableSearchAndFilters>
        <Flex w="100%" mr={8}>
          <Flex miw="240">
            <SearchInput
              name="formSearch"
              value={searchValue}
              handleClearValue={() => setSearchValue('')}
              onChange={e => setSearchValue(e.target.value)}
              placeholder={t('grants.forms.formsList.searchPlaceholder')}
              data-testid="form-search-input"
              status="default"
              inputSize="small"
            />
          </Flex>
          <Flex align="center" ml={8} miw="300">
            <SearchInput
              name="formIdsFilter"
              value={formIdsValue}
              handleClearValue={() => setFormIdsValue('')}
              onChange={e => setFormIdsValue(e.target.value)}
              placeholder={t('grants.forms.formsList.filterByFormIds')}
              data-testid="form-ids-filter-input"
              status="default"
              inputSize="small"
            />
          </Flex>
          <Flex align="center" ml={8}>
            <SearchFilterList
              options={partnerNameOptions}
              isLoading={isPartnerNameFilterLoading}
              selectedValues={selectedPartner ? [selectedPartner] : []}
              onChange={onPartnerChange}
              searchValue={partnerSearch.partnerNameSearch}
              onSearchChange={partnerSearch.onPartnerSearchChange}
              handleClearValue={partnerSearch.onClearPartnerSearchValue}
              onKeyDown={partnerSearch.handleKeyPress}
              onButtonClick={partnerSearch.onPartnersSearchButtonClick}
              testId="partner-search-filter"
              buttonText={t('typography.search')}
              placeholder={t('typography.search')}
              label={t('typography.partner')}
              placement="bottom"
            />
          </Flex>
        </Flex>
        {canCreateForm && (
          <Flex w="100%" justify="flex-end" align="center">
            <Button variant="secondary" size="small" data-testid="create-form-button" onClick={onCreateForm}>
              {t('grants.forms.formsList.createForm')}
            </Button>
          </Flex>
        )}
      </TableSearchAndFilters>

      <Table>
        <TableBody>
          {data?.map(form => (
            <TableRow key={form.id} onClick={() => onEditForm(form)} style={{ cursor: 'pointer' }}>
              <TableCell>{form.internalName || form.title || '-'}</TableCell>
              <TableCell>{form.partner?.name ?? '-'}</TableCell>
              <TableCell>
                {form.linkedTasksCount != null
                  ? `${form.linkedTasksCount} ${form.linkedTasksCount === 1 ? 'task' : 'tasks'}`
                  : '-'}
              </TableCell>
              <TableCell>
                {form.pages ? form.pages.reduce((sum, page) => sum + page.questions.length, 0) : '-'}
              </TableCell>
              <TableCell>{form.id}</TableCell>
              <TableCell>
                {form.createdAt
                  ? new Intl.DateTimeFormat('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }).format(new Date(form.createdAt))
                  : '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        nextPage={nextPage}
        previousPage={previousPage}
        totalResultsText={t('table.results', { count: totalResults ?? 0 })}
      />
    </TableProvider>
  )
}
